/**
 * Socket.IO connection to the `/epi-study-agent` namespace on the message-gateway.
 *
 * Deliberately does NOT reuse the singleton in
 * `@verantos-dev/app-state-base/namespaces/v2/researchAssistant` — that helper
 * is a module-level singleton whose second caller would collide with the
 * first. The epi-chat namespace is distinct enough to warrant its own socket.
 */

import { useEffect, useRef, useState } from 'react';
import { Socket, io } from 'socket.io-client';

import { getAuthClient, getToken, getUser } from '@verantos-dev/core-auth';
import type { AgUiEvent } from '@verantos-dev/feature-agent-chat';

const EPI_NAMESPACE = '/epi-study-agent';
const SOCKET_PATH = '/v1/message-gateway';

export type EpiSocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface UseEpiStudyAgentSocketOptions {
  /** Handler for inbound AG-UI events. Should be stable (wrapped in useCallback). */
  onEvent: (event: AgUiEvent) => void;
  enabled?: boolean;
}

export interface UseEpiStudyAgentSocketResult {
  status: EpiSocketStatus;
  send: (event: string, data: unknown) => void;
}

export const useEpiStudyAgentSocket = ({
  onEvent,
  enabled = true,
}: UseEpiStudyAgentSocketOptions): UseEpiStudyAgentSocketResult => {
  const [status, setStatus] = useState<EpiSocketStatus>('disconnected');
  const socketRef = useRef<Socket | null>(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let socket: Socket | undefined;

    const connect = async () => {
      setStatus('connecting');
      try {
        const client = await getAuthClient();
        const auth0User = await client.getUser();
        if (cancelled || !auth0User) return;
        const user = getUser(auth0User);
        const base = import.meta.env.VITE_BASE_SOCKET_URL as string | undefined;
        if (!base) {
          setStatus('error');
          return;
        }
        const url = new URL(base);
        url.pathname = EPI_NAMESPACE;

        socket = io(url.toString(), {
          path: SOCKET_PATH,
          // WebSocket stays primary, but allow the HTTP long-poll fallback: a
          // backgrounded tab throttles the WS ping/pong (silently dropping the
          // connection) and some infra closes idle WS frames — polling recovers
          // where a WS-only client dead-ends until a manual reload. The gateway
          // (python-socketio AsyncServer, upgrades enabled) accepts both.
          transports: ['websocket', 'polling'],
          reconnection: true,
          // Re-establish a transient drop quickly — inside the UI grace window
          // (see useGracefulConnectionStatus) so a self-healing reconnect never
          // surfaces as a "Not connected" flap. Defaults (1s/5s) are slower.
          reconnectionDelay: 500,
          reconnectionDelayMax: 4000,
          auth: async (cb) => cb({ token: await getToken() }),
          query: {
            userId: user.auth0Id,
            user: `${user.firstName} ${user.lastName}`,
          },
        });

        socket.on('connect', () => setStatus('connected'));
        socket.on('disconnect', () => setStatus('disconnected'));
        socket.on('connect_error', () => setStatus('error'));
        socket.on('ag_ui_event', (payload: AgUiEvent) => {
          onEventRef.current(payload);
        });
        socketRef.current = socket;
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    void connect();

    return () => {
      cancelled = true;
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
      socketRef.current = null;
      setStatus('disconnected');
    };
  }, [enabled]);

  const send = (event: string, data: unknown) => {
    socketRef.current?.emit(event, data);
  };

  return { status, send };
};
